import { useEffect, useState } from "react";
import { api } from "./lib/api";

type Svc = { expected: string; name: string; commit: string | null; started_at: string | null;
             is_self: boolean; reachable: boolean; error: string | null; mislabelled: boolean };
type Build = { build: string; first_seen: string | null };
type Payload = { services: Svc[]; field_builds: Build[] };
type Health = { app_user_id: string; name: string; parked: number; pending: number;
                build: string | null; reported_at: string | null };
type HealthPayload = { canvassers: Health[]; parked_total: number };

function ago(iso: string | null): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (!isFinite(ms)) return "—";
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// WHAT IS ACTUALLY RUNNING, RIGHT NOW.
//
// "Is the fix deployed?" was asked four times during the outage of 2026-08-10 and answered wrong
// twice, because the only evidence was an instance id buried in a log stream. A green deploy, a
// Restart that silently did nothing, and a service four days behind main all looked the same from
// the dashboard.
//
// An error is shown as an ERROR, never as a blank. During an incident "no version" would read as
// "nothing is deployed", which is the exact failure the empty-vs-error guard exists to prevent.
export function Versions() {
  const [data, setData] = useState<Payload | null>(null);
  const [health, setHealth] = useState<HealthPayload | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [healthErr, setHealthErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  const load = () => {
    setBusy(true); setErr(null); setHealthErr(null);
    api<Payload>("/deployments")
      .then(setData)
      .catch((e: any) => setErr(e?.message || String(e)))
      .finally(() => setBusy(false));
    // Separate request, separate error. A field-health failure must not blank the version table
    // and vice versa — during an incident you want whichever half still answers.
    api<HealthPayload>("/field-health")
      .then(setHealth)
      .catch((e: any) => setHealthErr(e?.message || String(e)));
  };
  useEffect(load, []);

  return (
    <div className="wrap">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <h2>What is deployed</h2>
        <button className="btn sm" onClick={load} disabled={busy}>{busy ? "Checking…" : "Refresh"}</button>
      </div>

      {err && <p className="err">Couldn't read the deployment status. {err}</p>}

      <h3>API services</h3>
      {!data && !err && <p className="muted">Checking both services…</p>}
      {data && (
        <table>
          <thead><tr><th>Service</th><th>Commit</th><th>Running since</th><th>Status</th></tr></thead>
          <tbody>
            {data.services.map((s) => (
              <tr key={s.expected}>
                <td>
                  {s.name}
                  {s.is_self && <span className="muted"> · this one</span>}
                  {s.name.includes("field") && <span className="muted"> · every crew member</span>}
                  {s.mislabelled && <div className="warn">configured as “{s.expected}” but answers to “{s.name}”</div>}
                </td>
                <td><code>{s.commit || "—"}</code></td>
                <td>{s.started_at ? `${ago(s.started_at)}` : "—"}</td>
                <td>{s.reachable
                  ? <span>up</span>
                  : <span className="warn" title={s.error || ""}>UNREACHABLE — {s.error}</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="muted">
        Names are what each service calls ITSELF, not what this page expected to find — so a staging
        portal cannot quietly show you a production service. “Running since” is the tell a restart
        actually happened: one that changes nothing leaves it unchanged.
      </p>

      <h3>Builds seen in the field</h3>
      {data && data.field_builds.length === 0 && <p className="muted">No build has reported yet.</p>}
      {data && data.field_builds.length > 0 && (
        <table>
          <thead><tr><th>Build</th><th>First seen</th></tr></thead>
          <tbody>
            {data.field_builds.map((b) => (
              <tr key={b.build}>
                <td><code>{b.build}</code></td>
                <td>{b.first_seen ? new Date(b.first_seen).toLocaleString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="muted">
        Newest first. A canvasser on a stale bundle looks completely normal in every other screen —
        this is the only place it shows.
      </p>

      <h3>Work stuck on phones</h3>
      {healthErr && <p className="err">Couldn't read the field app's health. {healthErr}</p>}
      {!health && !healthErr && <p className="muted">Checking…</p>}
      {health && health.canvassers.length === 0 && (
        <p className="muted">No phone has reported yet. Counts appear after a crew member syncs on a build that sends them.</p>
      )}
      {health && health.canvassers.length > 0 && (
        <>
          <p className={health.parked_total > 0 ? "warn" : "muted"}>
            {health.parked_total > 0
              ? `${health.parked_total} item${health.parked_total === 1 ? "" : "s"} parked across all phones — a bug is rejecting real knocks.`
              : "Nothing parked."}
          </p>
          <table>
            <thead><tr><th>Crew</th><th>Parked</th><th>Waiting</th><th>Build</th><th>Last reported</th></tr></thead>
            <tbody>
              {health.canvassers.map((c) => (
                <tr key={c.app_user_id}>
                  <td>{c.name}</td>
                  <td className={c.parked > 0 ? "warn" : undefined}>{c.parked}</td>
                  <td>{c.pending}</td>
                  <td><code>{c.build || "—"}</code></td>
                  <td>{ago(c.reported_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      <p className="muted">
        Parked means a 4xx sent those knocks to the dead letter. Nothing is lost — the phone retries
        them every 10 minutes — but a number that stays above zero means something is rejecting real
        work and nobody has noticed.
      </p>
    </div>
  );
}
